(ns manatee.annotation.components
  (:require
   [cljs.pprint :refer [pprint]]
   [clojure.set :refer [rename-keys]]
   [clojure.string :as string]
   [kee-frame.core :as kf]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.annotation.handlers :as handlers]
   [manatee.annotation.ids :as ids]
   [manatee.annotation.subs :as subs]
   [manatee.components.editors.subs :as editors-subs]
   [manatee.components.modal.handlers :as modal]
   [manatee.components.modal.view :refer [modal-container]]
   [manatee.components.notifications.container :as notifications]
   [manatee.domain.task :as task]
   [manatee.models.item :as item-model]
   [manatee.qa.handlers :as qa-handlers]
   [manatee.spec.core :as spec]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [rothko.button.component :refer [button]]
   [rothko.form2.checkbox.component :as checkbox]
   [rothko.form2.component :as form]
   [rothko.form2.input.component :as input]
   [rothko.form2.select.core :as select]
   [rothko.form2.select.multi-select.component :as multi-select]
   [rothko.form2.select.single-select.component :as single-select]
   [rothko.form2.validation :as validation]
   [rothko.form2.validation-message.component :as validation-message]
   [rothko.icon-animated.component :refer [spinner]]
   [rothko.icon.component :as icon]
   [rothko.layout.component :refer [layout]]
   [rothko.link.component :refer [link]]))

(defn loading-view-text
  []
  (rf/dispatch-sync [::handlers/set-loading-text-visible false])
  (rf/dispatch [::handlers/set-loading-text-visible-timeout 2000 true])
  #(when @(rf/subscribe [::subs/loading-text-visible])
     [:p "Loading work. Please note it may take up to 30 seconds."]))

(defmethod item-model/annotation-view
  :loading
  [& _]
  [:div.loading__view
   [spinner {:size 30}]
   [loading-view-text]])

(defmethod item-model/annotation-view
  :no-items
  [& _]
  [:p "You currently have no items to annotate in your queue, please "
   [link {:appearance #{:default}
          :theme :manatee
          :href (str "mailto:annotator-support@cambridge.org"
                     "?subject=No items in queue")}
    "contact support"]
   "."])

(defn back-button
  []
  [button {:appearance #{:secondary :full-width}
           :on-click #(rf/dispatch [::handlers/select-item-after-load :up])}
   "Back to part"])

(defn action-buttons
  [submit! modal-id submit-text]
  [:div.form-row.modal-form__actions
   [button
    {:appearance #{:primary :full-width}
     :disabled? @(rf/subscribe [::subs/loading?])
     :on-click submit!}
    submit-text]
   [button
    {:appearance #{:secondary :full-width}
     :attrs {:id :cancel-btn}
     :on-click #(rf/dispatch [::modal/hide modal-id])}
    "Cancel"]])

(defn rejection-buttons
  [submit! task-config]
  (let [custom-label (get-in task-config [:data :answer :rejections :modal-button])
        label (or custom-label "Reject")]
    (action-buttons submit! ids/reject-item-modal label)))

(defn submit-buttons
  [{:keys [submit!]}]
  (action-buttons submit! ids/submit-item-modal "Submit anyway"))

(defn approve-multi-buttons
  [{:keys [submit!]}]
  (action-buttons submit! ids/approve-multi-modal "Approve All"))

(defn reason-set [reason-val]
  (set (cond-> reason-val (keyword? reason-val) (vector))))

(defn no-issues
  "Validation added for TADA project where:
    * 'Reject' button is named 'Add label(s) and submit'
    * :no-issues is treated as single-rejection reason in multi-rejection select"
  [reason]
  (and (seqable? reason)
       (some #{:no-issues} reason)
       (> (count reason) 1)))

(defn rejection-form-validation
  [current-item incomplete-feedback? {:keys [reason other-reason]}]

  (cond
    (or (nil? reason)
        (= reason "default"))
    [:main-reason-error]

    (and (contains? (reason-set reason) :other)
         (string/blank? other-reason))
    [:other-reason-error]

    (and (= :part (:item-type current-item))
         incomplete-feedback?)
    [:answers-incomplete-feedback-error]

    (no-issues reason)
    [:no-issues-multi-error]

    :else
    ::validation/success))

(defn rejection-form-container
  [{:keys [item task-config rejection-options unannotated-answers]}]
  (let [multi-reject? (task/modifier task-config :multi-reject?)
        default-placeholder (if multi-reject? "Reason(s)" "Reason")
        custom-placeholder (get-in task-config [:data :answer :rejections :modal-placeholder])
        modal-placeholder (or custom-placeholder default-placeholder)]
    [form/form
     {::form/on-submit #(rf/dispatch [::handlers/reject-item
                                      {:item item
                                       :task-config task-config
                                       :form-values %
                                       :unannotated-answers unannotated-answers}])
      ::validation/fn (partial rejection-form-validation
                               item
                               @(rf/subscribe [::subs/incomplete-feedback?]))
      ::validation/messages {:main-reason-error "You must select a reason"
                             :other-reason-error "You must enter a reason when you have selected 'other'"
                             :answers-incomplete-feedback-error "One or more answers have feedback that has not been resolved"
                             :no-issues-multi-error "You must not select other labels when selecting 'No issues'"}}
     {:reason
      {:appearance #{:pills-inside-input :primary}
       ::form/name :reason
       ::form/placeholder modal-placeholder
       ::select/options rejection-options}
      :other-reason
      {:appearance #{:primary}
       ::form/name :other-reason
       ::form/type :text
       ::form/label "Other reason"
       ::input/type :textarea}}
     (fn [{:keys [::form/submit! ::validation/validate!] :as form-props} {:keys [reason other-reason]}]
       (let [validated (validate! form-props)]
         [:form
          [:div {:style {:display :flex :flex-direction :row}}
           (if multi-reject?
             [multi-select/field reason]
             [single-select/field reason])
           (when (contains? (reason-set (::form/value reason)) :other)
             [input/field (assoc other-reason ::form/disabled? false)])]
          (when (not= ::validation/success (::validation/form-messages validated))
            [validation-message/component
             (clojure.set/rename-keys validated {::validation/form-messages
                                                 ::validation/current-messages})])
          [rejection-buttons submit! task-config]]))]))

(defn reject-item
  [{:keys [item-type] :as item} task-config]
  (let [part-rejection-options (->> (get-in task-config [:data :part :rejections :reasons])
                                    (map #(rename-keys % {:label :representation})))
        answer-rejection-options (->> (get-in task-config [:data :answer :rejections :reasons])
                                      (map #(rename-keys % {:label :representation}))
                                      (sort-by :representation))]
    (when task-config
      [rejection-form-container
       {:item item
        :task-config task-config
        :rejection-options (if (= item-type :part) part-rejection-options answer-rejection-options)
        :unannotated-answers @(rf/subscribe
                               [::subs/unannotated-items-by-type :answer])}])))

(defn report-form
  [item]
  [form/form
   {::form/on-submit #(rf/dispatch [::handlers/report item %])}
   {:comment
    {:appearance #{:primary}
     ::form/name :comment
     ::form/type :text
     ::form/label "Comment"
     ::input/type :textarea
     ::validation/spec ::spec/non-blank-string
     ::validation/messages {::spec/non-blank-string "You must enter a comment."}}}
   (fn [{:keys [::form/submit!]} {:keys [comment]}]
     [:form.qa-report-form
      [input/field comment]
      [:div.qa-report-form__actions
       [button
        {:appearance #{:secondary :full-width}
         :on-click #(rf/dispatch [::modal/hide :report-item-modal])}
        "Cancel"]
       [button
        {:appearance #{:primary :full-width}
         :on-click submit!}
        "Submit"]]])])

(defn note-editor
  [{:keys [current-notes] :as item}]
  [form/form
   {::form/on-submit #(do (prn :submit-notes %) (rf/dispatch [::handlers/set-notes item %]))}
   {:notes {:appearance #{:primary}
            ::form/name :notes
            ::form/type :text
            ::form/label "Notes"
            ::form/initial-value current-notes
            ::input/type :textarea}}
   (fn [{:keys [::form/submit!]} {:keys [notes]}]
     [:form
      [input/field notes]
      [:div {:style {:display :flex :flex-direction :row}}
       [button {:appearance #{:secondary}
                :on-click #(rf/dispatch [::modal/hide :notes-modal])}
        (if current-notes "Discard changes" "Discard note")]
       [button {:appearance #{:primary}
                :on-click submit!}
        (if current-notes "Save changes" "Add note")]]])])

(defn submit-item-modal
  []
  (let [misspelled-words @(rf/subscribe [::editors-subs/misspelled-words])
        item @(rf/subscribe [::subs/current-item])]
    [modal-container
     {:appearance #{:primary}
      :id ids/submit-item-modal}
     [layout {:appearance #{:column}
              :class #{:report-form}}
      [:div
       [:h4 "Are you sure you want to submit the item?"]
       [:p "There are some misspelled words in the script:"]
       [:p.misspelled-words (string/join ", " misspelled-words)]
       [submit-buttons {:submit! #(rf/dispatch [::handlers/submit {:item item}])}]]]]))

(defn reject-item-modal
  [item]
  (let [task-config @(rf/subscribe [::task-config-subs/task-config (:task-config-id item)])
        multi-reject? (task/modifier task-config :multi-reject?)]
    [modal-container
     {:appearance #{:primary}
      :id ids/reject-item-modal}
     [notifications/alerts
      {:banner-id ids/rejection-alert-banner
       :max-alerts 1}]
     [layout {:appearance #{:column}
              :class #{:modal-form}}
      [:div ; div required by layout due to bug
       [:p (let [default-label (if multi-reject?
                                 "Please enter one or more rejection reasons"
                                 "Please enter rejection reason")
                 custom-label (get-in task-config [:data :answer :rejections :modal-title])]
             (or custom-label default-label))]
       [reject-item item task-config]]]]))

(defn report-item-modal
  [item]
  [modal-container
   {:appearance #{:primary}
    :id ids/report-item-modal}
   [notifications/alerts
    {:banner-id ids/report-alert-banner
     :max-alerts 1}]
   [layout {:appearance #{:column}
            :class #{:report-form}}
    [:div ; div required by layout due to bug
     [:p "Report annotation for review"]
     [report-form item]]]])

(defn notes-modal
  [{:keys [current-notes] :as item}]
  [modal-container
   {:appearance #{:primary}
    :id ids/notes-modal}
   [layout {:appearance #{:column :notes}
            :class #{:modal-form}}
    [:div
     [:p (if current-notes "Edit the note for this item" "Add a note to this item")]
     [note-editor item]]]])

(defn approve-multi-modal
  [edits]
  (let [edits-count (count edits)]
    [modal-container
     {:appearance #{:primary}
      :id ids/approve-multi-modal}
     [layout {:appearance #{:column}
              :class #{:modal-form}}
      [:div
       [:p (str "You are about to approve " edits-count (if (= 1 edits-count) " edit." " edits."))]]
      [approve-multi-buttons {:submit! #(rf/dispatch [::qa-handlers/approve-multi edits])}]]]))

(defn metadata-modal
  "Supply options that allow a user to add metadata to an annotation separately to
  the main form"
  [item]
  [modal-container
   {:appearance #{:primary}
    :id :metadata-modal}
   [notifications/alerts
    {:banner-id :metadata-modal
     :max-alerts 1}]
   [:div.layout.column
    [item-model/metadata-options
     item
     {:close-fn #(rf/dispatch [::modal/hide :metadata-modal])
      :submit-fn #(rf/dispatch [::handlers/add-metadata %])}]]])

(defn alert-container
  []
  [notifications/alerts
   {:banner-id ids/annotation-alert-banner
    :max-alerts 1}])

(defn form-settings
  "The form validation flow:
  1. `::handlers/form-change` stores validation status of the current edit as the changes are made.
  2. on submit, `::handlers/validate` checks the aforementioned validation status and, if invalid, sets validate-now flag.
  3. `::subs/validate-now?` returns the flag and, if it's set, we reset it and show validation messages."
  [{:keys [id qa-edit-id] :as item}]
  {::form/on-form-change (fn [{:keys [::form/current ::validation/valid?]}]
                           (rf/dispatch [::handlers/form-change item current valid?]))
   ::validation/validate-now? (when @(rf/subscribe [::subs/validate-now? id qa-edit-id])
                                (rf/dispatch [::handlers/reset-validate-now id qa-edit-id])
                                true)})

(defn display-raw-item-data
  "Render the button that allows the user to view the raw item data."
  [_ _]
  (let [keys-to-be-hidden #{:score :full-transcript :item-task}
        expanded? (r/atom false)
        button-attributes {:on-click #(swap! expanded? not)
                           :style {:width "20px"}}]
    (fn [task-config item]
      (if @expanded?
        [:<>
         [:button button-attributes "-"]
         [:pre (with-out-str (-> (apply dissoc item keys-to-be-hidden)
                                 (assoc :task-name (:task-name task-config))
                                 (pprint)))]]
        [:button button-attributes "+"]))))

(defn ai-hybrid-checkbox
  [init-value]
  {:appearance #{:annotation__view :checkbox__ai-hybrid}
   ::form/name :ai-hybrid?
   ::form/initial-value init-value
   ::checkbox/option true
   ::checkbox/label "AI-hybrid?"
   :tooltip "Is this item a combination of human and AI?"})

(defn info-links
  "A hover drop-down menu for links to task documentation."
  [task-documents task-config-id]
  [:div.info-dropdown {:style {:float :right}}
   [:div.info-dropdown-button
    [:span
     [:a "Info "]
     [icon/icon {:icon :angle-down
                 :size 14}]]
    [:div.info-dropdown-content {:style {:right 0}}
     (for [{:keys [id name link] :as _doc} task-documents]
       [:div {:key id}
        (if link
          [:a.info-dropdown-link {:style {:text-decoration :none}
                                  :href link
                                  :target "_blank"}
           name]
          [:a.info-dropdown-link {:style {:text-decoration :none}
                                  :href (kf/path-for [:api/task-document :task-config-id task-config-id :document-id id])
                                  :target "_blank"
                                  :download (str name ".pdf")
                                  :type "application/pdf"}
           name])])]]])
